import { ReactElement, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { ContentLabelField } from '@hopskipdrive/react-components';
import * as Yup from 'yup';

import IncidentClassificationDropdown from '../../../components/IncidentClassificationDropdown';
import IncidentSubClassificationDropdown from '../../../components/IncidentSubClassificationDropdown';
import {
  CombinedIncidentClassification,
  IncidentClassification,
  IncidentSubclassification,
} from '../../../types/incidents';

interface ClassificationIdFieldProps {
  defaultValue?: CombinedIncidentClassification;
}

export const classificationIdValidation = Yup.number()
  .required('Incident classification selection is required')
  .nullable();

export const ClassificationIdField = ({ defaultValue }: ClassificationIdFieldProps): ReactElement => {
  const [classification, setClassification] = useState<IncidentClassification | null>(
    defaultValue?.classification || null
  );
  const [subClassification, setSubclassification] = useState<IncidentSubclassification | null>(
    defaultValue?.subclassification || null
  );

  const { control } = useFormContext();
  const { field: fieldClassification, fieldState: fieldClassificationState } = useController({
    name: 'classificationId',
    control,
    defaultValue: defaultValue?.classification.id || null,
  });

  const { field: fieldSubclassification, fieldState: fieldSubclassificationState } = useController({
    name: 'subclassificationId',
    control,
    defaultValue: defaultValue?.subclassification?.id || null,
  });

  return (
    <>
      <ContentLabelField
        id="classification"
        title="Incident classification"
        editMode={true}
        required={true}
        editView={
          <IncidentClassificationDropdown
            value={classification}
            onChange={(selected) => {
              setClassification(selected);
              fieldClassification.onChange(selected?.id || null);

              setSubclassification(null);
              fieldSubclassification.onChange(null);
            }}
            error={!!fieldClassificationState.error || !!fieldSubclassificationState.error}
            helperText={fieldClassificationState.error?.message || fieldSubclassificationState.error?.message}
          />
        }
      />

      {classification && (
        <ContentLabelField
          id="subClassification"
          title="Subclassification"
          editMode={true}
          required={classification?.requiresSubclassification}
          editView={
            <IncidentSubClassificationDropdown
              value={subClassification || null}
              sortField="postition"
              classificationId={classification.id}
              requiresSubclassification={classification?.requiresSubclassification}
              subclassificationsCount={classification?.subclassificationsCount}
              onChange={(selected) => {
                setSubclassification(selected);
                fieldSubclassification.onChange(selected?.id || null);
              }}
              error={!!fieldClassificationState.error || !!fieldSubclassificationState.error}
              helperText={fieldClassificationState.error?.message || fieldSubclassificationState.error?.message}
            />
          }
        />
      )}
    </>
  );
};
